import {
    Component,
    EventEmitter,
    Input,
    InputSignal,
    OnChanges,
    OnInit,
    Output,
    Signal,
    SimpleChanges,
    computed,
    output,
} from '@angular/core';
import { AbstractControl, FormGroup } from '@angular/forms';
import { MatCheckboxChange } from '@angular/material/checkbox';
import {
    DatiqInputService,
    IDatiqInputErrorMessages,
    IDatiqOption,
} from '@dat/datiq-components-pkg/utils';
import { noop } from 'lodash';
import { Observable } from 'rxjs';
import { tap } from 'rxjs/operators';
import { emptyDatiqSelect } from './models/datiq-select-model-empty';
import {
    DatiqSelectEnumStyle,
    DatiqSelectTypesInput,
    DatiqSelectTypesSelect,
    IDatiqSelect,
} from './models/datiq-select.interface';
import { DatiqSelectService } from './services/datiq-select.service';

@Component({
    selector: 'datiq-select',
    templateUrl: './datiq-select.component.html',
    styleUrls: ['./datiq-select.component.scss'],
})
export class DatiqSelectComponent implements OnInit, OnChanges {
    @Input()
    settings: IDatiqSelect;
    @Input()
    formGroup: FormGroup;
    @Input()
    inputControl: AbstractControl;
    @Input()
    customErrorMessage?: string;
    optionSelected = output<IDatiqOption>();
    multiSelected = output<IDatiqOption[]>();

    focused = output();

    inputStatusControl$: Observable<unknown>;

    panelClasses: string[] = [];

    selectControl: AbstractControl;
    selectControl$: Observable<DatiqSelectTypesSelect>;
    inputControl$: Observable<DatiqSelectTypesInput>;
    formGroup$: Observable<{ [key: string]: DatiqSelectTypesInput }>;
    errorMessages: IDatiqInputErrorMessages;
    selectedValue: string;
    chips: IDatiqOption[];

    isDark: Signal<boolean> = computed(() => {
        return this.settings.style === DatiqSelectEnumStyle.Dark;
    });

    panelOpen: boolean = false;

    constructor(
        private _inputService: DatiqInputService,
        private _service: DatiqSelectService,
    ) {}

    ngOnChanges(changes: SimpleChanges): void {
        if (
            !!changes['settings']?.currentValue &&
            !changes['settings']?.isFirstChange()
        ) {
            if ((changes['settings'].currentValue as IDatiqSelect).disabled) {
                this.inputControl.disable({ emitEvent: false });
                this.selectControl.disable();
            } else {
                this.inputControl.enable({ emitEvent: false });
                this.selectControl.enable();
            }

            this.inputControl.setValue(
                (changes['settings'].currentValue as IDatiqSelect).value,
            );
        }
    }

    ngOnInit(): void {
        this.settings = !!this.settings
            ? this.settings
            : { ...emptyDatiqSelect };

        this.formGroup = !!this.formGroup
            ? this.formGroup
            : this._inputService.createForm();

        this.inputControl = !!this.inputControl
            ? this.inputControl
            : this._inputService.createControl(this.settings, this.formGroup);

        this._service.addValidatorsToControl(
            this.settings,
            this.inputControl,
            this.formGroup,
        );

        this.selectControl = this._service.createSelectControl(
            this.settings,
            this.formGroup,
        );

        this.selectedValue = this.selectControl.value;

        this.selectControl$ = this.selectControl.valueChanges.pipe(
            tap((selectValue: DatiqSelectTypesSelect) => {
                this.syncInputValueToSelectValue(selectValue);
            }),
        );

        this.inputControl$ = this.inputControl.valueChanges.pipe(
            tap((inputValue: DatiqSelectTypesInput) => {
                this.syncSelectValueToInputValue(inputValue);
            }),
        );

        this.inputStatusControl$ = this.inputControl.statusChanges.pipe(
            tap(() => {
                this.inputControl.disabled
                    ? this.selectControl.disable()
                    : this.selectControl.enable();

                this.inputControl.pristine
                    ? this.selectControl.markAsPristine()
                    : noop();
                this.inputControl.untouched
                    ? this.selectControl.markAsUntouched()
                    : noop();
            }),
        );

        this.chips = [];

        this.panelClasses = [
            'select',
            `${this.settings?.id}__panel`,
            ...(this.settings?.style === DatiqSelectEnumStyle.Dark
                ? ['!bg-dat-grey-900', 'text-white', 'border-dat-dark-800']
                : []),
        ];
    }

    onSelectionChange(optionValue: string): void {
        const option: IDatiqOption = this.settings.options.find(
            (el) => el.value === optionValue,
        );
        this.optionSelected.emit(option);
    }

    syncInputValueToSelectValue(selectValue: DatiqSelectTypesSelect) {
        const outOfSync: boolean = this._service.isInputOutOfSyncWithSelect(
            selectValue,
            this.inputControl.value,
            this.settings,
        );
        const syncedInputValue: DatiqSelectTypesInput =
            this._service.getSyncedInputValue(selectValue, this.settings);

        outOfSync ? this.updateInputSync(syncedInputValue, selectValue) : null;
    }

    updateInputSync(
        syncedInputValue: DatiqSelectTypesInput,
        selectValue: DatiqSelectTypesSelect,
    ) {
        this.inputControl.setValue(syncedInputValue);
        this.settings = {
            ...this.settings,
            value: syncedInputValue,
        };

        this.selectedValue = this.settings?.multiple
            ? this.selectedValue
            : (selectValue as string);
    }

    syncSelectValueToInputValue(inputValue: DatiqSelectTypesInput) {
        const outOfSync: boolean = this._service.isInputOutOfSyncWithSelect(
            this.selectControl.value,
            inputValue,
            this.settings,
        );
        const syncedSelectValue: DatiqSelectTypesSelect =
            this._service.getSyncedSelectValue(inputValue, this.settings);

        outOfSync ? this.updateSelectSync(syncedSelectValue, inputValue) : null;
    }

    updateSelectSync(
        syncedSelectValue: DatiqSelectTypesSelect,
        inputValue: DatiqSelectTypesInput,
    ) {
        this.selectControl.setValue(syncedSelectValue);
        this.settings = {
            ...this.settings,
            value: inputValue,
        };
        this.selectedValue = this.settings?.multiple
            ? this.selectedValue
            : (inputValue as IDatiqOption)?.value ?? '';
    }

    remove(option: IDatiqOption): void {
        !!this.settings.multiple
            ? this.updateMultipleValues(false, option)
            : null;
    }

    isMultiSelected(option: IDatiqOption): boolean {
        return this._service.optionIsInOptions(
            option,
            this.settings?.value as IDatiqOption[],
        );
    }

    selectMulti(evt: MatCheckboxChange, option: IDatiqOption): void {
        const shouldAdd = !!evt?.checked;
        this.updateMultipleValues(shouldAdd, option);
    }

    updateInputControl() {
        this.inputControl.setValue(this.settings.value);
    }

    updateMultipleValues(shouldAdd: boolean, option: IDatiqOption) {
        this.settings = this._service.updateCheckedValues(
            shouldAdd,
            option,
            this.settings,
        );

        this.selectControl.setValue(
            this._service.getMultipleSelectedValues(
                this.settings.value as IDatiqOption[],
            ),
        );

        this.multiSelected.emit(this.settings.value as IDatiqOption[]);
        this.updateInputControl();
    }

    selectSingle(option: IDatiqOption): void {
        this.updateSingleValue(option);
    }

    updateSingleValue(value: IDatiqOption): void {
        this.settings = { ...this.settings, value };
        this.selectedValue = this._service.getSelectedValueFromSettings(
            this.settings,
        );
        this.selectControl.setValue(this.selectedValue);
    }

    getErrorMessage(): string {
        this.errorMessages = this._inputService.getErrorMessagesFromSettings(
            this.settings,
        );
        return this._inputService.getErrorMessage(
            this.inputControl,
            this.errorMessages,
        );
    }

    onOpenChanged(open: boolean) {
        this.panelOpen = open;
    }
}
